import { CalendarIcon, CurrencyDollarIcon, EnvelopeIcon, UsersIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';
import C from 'utils/values/constant.util';

import CN from './AnimatedEmailBanner.module.scss';

const STAT_TILES = [
  {
    id: 'campaigns',
    labelKey: 'animatedEmailBanner.scenes.analytics.tiles.campaignsSent',
    value: '4',
    Icon: EnvelopeIcon,
  },
  {
    id: 'clients',
    labelKey: 'animatedEmailBanner.scenes.analytics.tiles.clientsEmailed',
    value: '2,267',
    Icon: UsersIcon,
  },
  {
    id: 'bookings',
    labelKey: 'animatedEmailBanner.scenes.analytics.tiles.bookingsReceived',
    value: '86',
    Icon: CalendarIcon,
  },
  {
    id: 'revenue',
    labelKey: 'animatedEmailBanner.scenes.analytics.tiles.revenueGenerated',
    value: '6,671',
    isCurrency: true,
    Icon: CurrencyDollarIcon,
  },
];

const TABLE_ROWS = C([
  {
    dateKey: 'animatedEmailBanner.scenes.analytics.rows.row1.date',
    campaignKey: 'animatedEmailBanner.scenes.analytics.rows.row1.campaign',
    sent: '571',
    bookings: '24',
    revenue: '1,834',
  },
  {
    dateKey: 'animatedEmailBanner.scenes.analytics.rows.row2.date',
    campaignKey: 'animatedEmailBanner.scenes.analytics.rows.row2.campaign',
    sent: '729',
    bookings: '31',
    revenue: '2,407',
  },
  {
    dateKey: 'animatedEmailBanner.scenes.analytics.rows.row3.date',
    campaignKey: 'animatedEmailBanner.scenes.analytics.rows.row3.campaign',
    sent: '535',
    bookings: '26',
    revenue: '1,915',
  },
]);

const BROWSER_DOTS = ['red', 'yellow', 'green'];
const BLOB_VARIANTS = ['1', '2'];
const SLIDE_INDICATORS = ['first', 'second', 'third'];

export const AnimatedEmailBanner = () => {
  const { t } = useTranslation();

  return (
    <div data-bem="AnimatedEmailBanner" className={CN.assetFrame}>
      {/* Decorative browser controls */}
      <div className={CN.browserBar}>
        {BROWSER_DOTS.map((color) => (
          <div key={`browser-dot-${color}`} className={CN.browserDot} />
        ))}
      </div>

      <div className={CN.stage}>
        {/* Decorative blobs in the background */}
        {BLOB_VARIANTS.map((variant) => (
          <div key={`blob-${variant}`} className={cn(CN.blob, CN[`blob${variant}`])} />
        ))}

        {/* SCENE 1 */}
        <div className={cn(CN.scene, CN.scene1)}>
          <div className={CN.sceneLabel}>
            <span className={CN.stepNum}>1</span>
            {t('animatedEmailBanner.scenes.personalise.label')}
          </div>

          <div className={CN.templateStack}>
            <div className={cn(CN.templateCard, CN.tmpl1)}>
              <div className={CN.templateBanner}>{t('animatedEmailBanner.scenes.personalise.templates.retail.banner')}</div>
              <div className={CN.templateBody}>
                <div className={cn(CN.bullet, CN.long)} />
                <div className={cn(CN.bullet, CN.short)} />
                <div className={CN.productRow}>
                  <div className={CN.productItem} />
                  <div className={CN.productItem} />
                </div>
                <div className={cn(CN.bullet, CN.long)} />
                <div className={cn(CN.bullet, CN.long)} />
                <div className={cn(CN.bullet, CN.short)} />
                <div className={CN.ctaBlock} />
              </div>
            </div>

            <div className={cn(CN.templateCard, CN.tmpl2)}>
              {/* Decorative hero background container */}
              <div className={CN.templateHero} />
              <div className={CN.templateBody}>
                <div className={CN.salonName}>{t('animatedEmailBanner.scenes.personalise.templates.hairShed.salonName')}</div>
                <div className={CN.greeting}>{t('animatedEmailBanner.scenes.personalise.templates.hairShed.greeting')}</div>
                {/* Decorative UI elements to represent email body content */}
                <div className={cn(CN.bodyLine, CN.full)} />
                <div className={cn(CN.bodyLine, CN.full)} />
                <div className={cn(CN.bodyLine, CN.mid)} />
                <div className={cn(CN.bodyLine, CN.short)} />

                <div className={CN.ctaBlock}>{t('animatedEmailBanner.scenes.personalise.templates.hairShed.cta')}</div>
              </div>
              <div className={CN.selectionRing} />
            </div>

            <div className={cn(CN.templateCard, CN.tmpl3)}>
              <div className={CN.templateBanner}>
                {t('animatedEmailBanner.scenes.personalise.templates.selfCare.banner')}
                <div className={CN.sub}>{t('animatedEmailBanner.scenes.personalise.templates.selfCare.sub')}</div>
              </div>

              <div className={CN.templateBody}>
                <div className={CN.promoHeadline}>{t('animatedEmailBanner.scenes.personalise.templates.selfCare.headline')}</div>

                <div className={CN.promoBullet}>{t('animatedEmailBanner.scenes.personalise.templates.selfCare.bullets.freshCuts')}</div>
                <div className={CN.promoBullet}>{t('animatedEmailBanner.scenes.personalise.templates.selfCare.bullets.vibrantColour')}</div>
                <div className={CN.promoBullet}>{t('animatedEmailBanner.scenes.personalise.templates.selfCare.bullets.treatments')}</div>
                <div className={CN.promoBullet}>{t('animatedEmailBanner.scenes.personalise.templates.selfCare.bullets.luxuriousBlowouts')}</div>

                <div className={CN.ctaBlock} />
              </div>
            </div>
          </div>
        </div>

        {/* SCENE 2 */}
        <div className={cn(CN.scene, CN.scene2)}>
          <div className={CN.sceneLabel}>
            <span className={CN.stepNum}>2</span>
            {t('animatedEmailBanner.scenes.customise.label')}
          </div>

          <div className={CN.composeCard}>
            <div className={CN.composeHeader}>
              <div className={CN.label}>{t('animatedEmailBanner.scenes.customise.to')}</div>
              <div className={CN.value}>{t('animatedEmailBanner.scenes.customise.activeClients')}</div>
            </div>

            <div className={CN.composeSubject}>{t('animatedEmailBanner.scenes.customise.subject')}</div>

            <div className={CN.composeHero} />

            <div className={CN.composeBody}>
              <div className={CN.salonNameLarge}>{t('animatedEmailBanner.scenes.customise.salonName')}</div>

              <div className={CN.greetingLine}>
                {t('animatedEmailBanner.scenes.customise.greeting')} <span className={CN.mergeTag} />,
              </div>

              <div className={cn(CN.bodyLine, CN.full)} />
              <div className={cn(CN.bodyLine, CN.mid)} />

              <div className={CN.composeCta}>
                {t('animatedEmailBanner.scenes.customise.cta')} {S.arrowRight}
              </div>
            </div>
          </div>
        </div>

        {/* SCENE 3  */}
        <div className={cn(CN.scene, CN.scene3)}>
          <div className={CN.sceneLabel}>
            <span className={CN.stepNum}>3</span>
            {t('animatedEmailBanner.scenes.analytics.label')}
          </div>

          <div className={CN.analyticsCard}>
            <div className={CN.analyticsTitle}>
              {S.barChart} {t('animatedEmailBanner.scenes.analytics.analyticsTitle')}
            </div>

            <div className={CN.analyticsSubtitle}>{t('animatedEmailBanner.scenes.analytics.analyticsSubtitle')}</div>

            <div className={CN.statsGrid}>
              {STAT_TILES.map((tile) => (
                <div key={tile.id} className={CN.statTile}>
                  <div className={CN.tileIcon}>
                    <tile.Icon />
                  </div>

                  <div className={CN.tileLabel}>{t(tile.labelKey)}</div>

                  <div className={cn(CN.tileValue, tile.isCurrency && CN.revenue)}>
                    {tile.isCurrency && <>{t('animatedEmailBanner.currency')}</>}
                    {tile.value}
                  </div>
                </div>
              ))}
            </div>

            <div className={CN.miniTable}>
              <table className={CN.table}>
                <thead>
                  <tr className={CN.tableHeader}>
                    <th>{t('animatedEmailBanner.scenes.analytics.tableHeaders.date')}</th>
                    <th>{t('animatedEmailBanner.scenes.analytics.tableHeaders.campaign')}</th>
                    <th>{t('animatedEmailBanner.scenes.analytics.tableHeaders.sent')}</th>
                    <th>{t('animatedEmailBanner.scenes.analytics.tableHeaders.bookings')}</th>
                    <th>{t('animatedEmailBanner.scenes.analytics.tableHeaders.revenue')}</th>
                  </tr>
                </thead>

                <tbody>
                  {TABLE_ROWS.map((row, index) => (
                    <tr key={index} className={CN.tableRow}>
                      <td>{t(row.dateKey)}</td>
                      <td className={CN.campaignName}>{t(row.campaignKey)}</td>
                      <td>{row.sent}</td>
                      <td>{row.bookings}</td>
                      <td className={CN.revenueCell}>
                        {t('animatedEmailBanner.currency')}
                        {row.revenue}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Progress Dots */}
        <div className={CN.progressDots}>
          {SLIDE_INDICATORS.map((id) => (
            <div key={`progress-dot-${id}`} className={CN.progressDot} />
          ))}
        </div>
      </div>
    </div>
  );
};
